import { useTranslation } from '@app/app/i18n';
import i18n from '@app/app/i18n/config';
import { useMemo } from 'react';

type PageMetaOptions = {
  /**
   * Namespace для переводов (например, 'main', 'features', 'about', 'pricing')
   * Если указан, title и meta.description будут автоматически браться из переводов
   */
  namespace?: string;
  /**
   * Кастомный title (если не указан namespace или нужно переопределить)
   */
  title?: string;
  /**
   * Кастомный description (если не указан namespace или нужно переопределить)
   */
  description?: string;
  /**
   * Кастомный og:title (по умолчанию используется title)
   */
  ogTitle?: string;
  /**
   * Кастомный og:description (по умолчанию используется description)
   */
  ogDescription?: string;
  /**
   * Кастомный og:image (по умолчанию используется дефолтное изображение)
   */
  ogImage?: string;
};

// Маппинг языков для og:locale (только основной, alternate делаются на бэкенде)
const getOgLocale = (langCode: string): string => {
  const localeMap: Record<string, string> = {
    en: 'en_US',
    es: 'es_ES',
    fr: 'fr_FR',
    de: 'de_DE',
    'pt-BR': 'pt_BR',
    ru: 'ru_RU',
    ar: 'ar_AR',
  };
  return localeMap[langCode] || 'en_US';
};

/**
 * Хук для генерации мета-тегов страницы для React Helmet
 *
 * Автоматически берет title и meta.description из переводов, если указан namespace.
 * При смене языка мета-теги обновляются автоматически (благодаря зависимости от i18n.language).
 *
 * Что делает фронтенд (через Helmet):
 * - title, meta description
 * - og:title, og:description, og:url, og:locale (основной)
 *
 * Что делает бэкенд (в HTML):
 * - html lang и dir
 * - canonical и hreflang
 * - og:locale:alternate (все alternate локали)
 *
 * @example
 * // Автоматически берет из переводов
 * const meta = usePageMeta({ namespace: 'main' });
 *
 * // Или кастомные значения
 * const meta = usePageMeta({ title: 'Custom Title', description: 'Custom Description' });
 */
export const usePageMeta = (options: PageMetaOptions = {}) => {
  const { namespace, title: customTitle, description: customDescription } = options;
  const { t } = useTranslation(namespace || 'common');
  const currentLanguage = i18n.language;

  // Используем useMemo для пересчета при смене языка
  return useMemo(() => {
    // Берем title из переводов, если указан namespace, иначе используем кастомный
    const title = namespace ? t('title') : customTitle || 'Wonderslide';

    // Берем description из переводов, если указан namespace, иначе используем кастомный
    // i18next поддерживает вложенные ключи через точку: 'meta.description'
    const description = namespace ? t('meta.description') || '' : customDescription || '';

    // Формируем полный URL для og:url
    const ogUrl = typeof window !== 'undefined' ? window.location.href : '';

    return {
      title,
      description,
      ogTitle: options.ogTitle || title,
      ogDescription: options.ogDescription || description,
      ogImage: options.ogImage || 'https://classic.wonderslide.com/static/i/ws_og_img.png',
      ogUrl,
      ogLocale: getOgLocale(currentLanguage),
    };
  }, [
    namespace,
    customTitle,
    customDescription,
    options.ogTitle,
    options.ogDescription,
    options.ogImage,
    t,
    currentLanguage,
  ]);
};
